/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import { classnames as cx } from '@ba-ui-toolkit/utils';
import { DragSource, DropTarget } from 'react-dnd';
import Row from './Row';
import styles from './simpletable.scss';

const stClassName = styles.stClassName.replace(/"/g, '');

const style = {
	width: '100%',
	cursor: 'move',
};

/** Calculate position percentage of hovering on current row */
function getHoveredYPercent(props, monitor, component) {
	const hoverBoundingRect = findDOMNode(component).getBoundingClientRect();
	const clientOffset = monitor.getClientOffset();
	const hoverClientY = clientOffset.y - hoverBoundingRect.top;
	const hoveredYPercent =
		(hoverClientY / (hoverBoundingRect.bottom - hoverBoundingRect.top)) * 100;

	return hoveredYPercent;
}

/** Methods trigger when Element is dragged */
const draggableSource = {
	beginDrag(props) {
		return {
			rowKey: props.rowKey,
		};
	},
};
const connectDS = (connect, monitor) => ({
	connectDragSource: connect.dragSource(),
	isDragging: monitor.isDragging(),
});

/** Methods trigger when Element is dropped */
const connectDT = (connect, monitor) => ({
	connectDropTarget: connect.dropTarget(),
	isOver: monitor.isOver(),
	canDrop: monitor.canDrop(),
});
const draggableComponentTarget = {
	drop(props, monitor, component) {
		const hoveredYPercent = getHoveredYPercent(props, monitor, component);

		const sourceId = monitor.getItem().rowKey;
		const targetId = props.rowKey;
		if (sourceId === targetId) return;
		// move down
		if (sourceId < targetId) {
			if (hoveredYPercent > 50) {
				props.onRowMove(sourceId, targetId);
			} else {
				props.onRowMove(sourceId, targetId - 1);
			}
			// move up
		} else if (sourceId > targetId) {
			if (hoveredYPercent < 50) {
				props.onRowMove(sourceId, targetId);
			} else {
				props.onRowMove(sourceId, targetId + 1);
			}
		}
	},
	hover(props, monitor, component) {
		const hoveredAbove = getHoveredYPercent(props, monitor, component) <= 50;
		const className = hoveredAbove ? cx('hoveredAbove') : cx('hoveredBelow');
		findDOMNode(component).classList.add(className);
	},
};
/** Custom Draggable Component */
@DropTarget('draggableList', draggableComponentTarget, connectDT)
@DragSource('draggableList', draggableSource, connectDS)
export default class RowDnD extends Component {
	static propTypes = {
		connectDropTarget: PropTypes.func.isRequired,
		connectDragSource: PropTypes.func.isRequired,
		isDragging: PropTypes.bool,
		isOver: PropTypes.bool,
		canDrop: PropTypes.bool,
		/** Props you like to import */
		index: PropTypes.number,
		onRowMove: PropTypes.func,
		rowLength: PropTypes.number,
		name: PropTypes.string,
	};

	onSelect() {
		this.setState({
			selected: !this.state.selected,
		});
	}

	render() {
		const {
			connectDragSource,
			connectDropTarget,
			isDragging,
			canDrop,
			isOver,
			rowLength,
			...other
		} = this.props;

		/** Essential to make Componet both Draggable and Droppable */
		return (
			<Row
				className={cx(`${stClassName}-tbody-row`, {
					dragging: isDragging,
					active: canDrop && isOver,
				})}
				style={{ ...style }}
				{...other}
				ref={instance => {
					const node = findDOMNode(instance);
					connectDragSource(node);
					connectDropTarget(node);
				}}
			/>
		);
	}
}
